import { useState } from 'react';
import { Pill } from '@/components/Pill';
import { formatDollars } from '@/lib/useRetailStores';
import {
  formatHour,
  heatLevel,
  heatMax,
  useHomeHourly,
  type HomeFilters,
} from '@/lib/useHomeDashboard';
import { useChartPalette } from './chartColors';
import { TileMessage, TileShell, TileSkeleton } from './TileShell';
import { MUTED_TEXT, NOT_AVAILABLE } from './tileText';

const DEFAULT_ROWS = 15;

export function SalesByHour({ filters }: { filters: HomeFilters }) {
  const { data, isLoading, unavailable } = useHomeHourly(filters);
  const palette = useChartPalette();
  const [showAll, setShowAll] = useState(false);

  const stores = data?.stores ?? [];
  const hours = data?.hours ?? [];
  const visible = showAll ? stores : stores.slice(0, DEFAULT_ROWS);
  // One scale across every store, so the toggle does not re-color the grid.
  const max = heatMax(stores);

  const toggle =
    stores.length > DEFAULT_ROWS ? (
      <Pill onClick={() => setShowAll((v) => !v)} aria-pressed={showAll} variant={showAll ? 'selected' : 'outline'}>
        {showAll ? 'Show top 15 stores' : 'Show all stores'}
      </Pill>
    ) : null;

  return (
    <TileShell
      label="Stores"
      title="Sales by hour"
      caption={data ? `Average per hour, ${data.range.label}` : 'Average per hour'}
      action={toggle}
    >
      {isLoading ? (
        <TileSkeleton rows={6} />
      ) : unavailable ? (
        <TileMessage>{NOT_AVAILABLE}</TileMessage>
      ) : stores.length === 0 || hours.length === 0 ? (
        <TileMessage>No hourly sales yet.</TileMessage>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="border-separate border-spacing-[2px]" style={{ minWidth: 520 }}>
              <thead>
                <tr>
                  <th className="sticky left-0 bg-amp-white dark:bg-amp-dark-surface" />
                  {hours.map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className={`min-w-[32px] text-center text-[12px] font-semibold ${MUTED_TEXT}`}
                    >
                      {formatHour(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => {
                  const byHour = new Map(s.cells.map((c) => [c.hour, c.avg_sales]));
                  return (
                    <tr key={s.store_no}>
                      <th
                        scope="row"
                        className="sticky left-0 max-w-[160px] truncate bg-amp-white pr-2 text-left text-[13px] font-normal text-amp-ink dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                      >
                        {s.store_name}
                      </th>
                      {hours.map((h) => {
                        const v = byHour.get(h) ?? 0;
                        const tip = `${s.store_name}, ${formatHour(h)}: ${formatDollars(v)} average`;
                        return (
                          <td
                            key={h}
                            title={tip}
                            aria-label={tip}
                            className="h-6 rounded-sm"
                            style={{ backgroundColor: palette.heat[heatLevel(v, max)] }}
                          />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data?.note && <p className={`text-[12px] ${MUTED_TEXT}`}>{data.note}</p>}
          <div className={`flex items-center gap-2 text-[12px] ${MUTED_TEXT}`}>
            <span>Slow</span>
            {palette.heat.map((c) => (
              <span key={c} className="h-3 w-6 rounded-sm" style={{ backgroundColor: c }} />
            ))}
            <span>Busy</span>
          </div>
        </>
      )}
    </TileShell>
  );
}
